import { useMemo } from 'react';

import { ErrorText } from '../../styles';
import { SavedSnippetFieldKind, UniformListItem } from '../../api/types';
import { JobDraftV2 } from '../../context';

import {
  ActionDivider,
  ContentWrap,
  FieldGroup,
  FieldLabel,
  InstructionsTextarea,
  LinkButton,
  RequiredMark,
  StepContent,
  StepTitle,
  TextareaActions,
  UniformGrid,
  UniformImage,
  UniformLabel,
  UniformTile,
} from './styles';

type SnippetApplyHandler = (body: string) => void;

type InstructionsStepProps = {
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  uniforms: UniformListItem[];
  uniformsLoading: boolean;
  uniformsError: string | null;
  onBrowseSaved: (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    onApply: SnippetApplyHandler,
  ) => void;
  onSaveSnippet: (
    fieldKind: SavedSnippetFieldKind,
    title: string,
    currentBody: string,
  ) => void;
};

const InstructionsStep = ({
  jobs,
  onUpdateJobs,
  uniforms,
  uniformsLoading,
  uniformsError,
  onBrowseSaved,
  onSaveSnippet,
}: InstructionsStepProps) => {
  const job = jobs[0];

  const update = <K extends keyof JobDraftV2>(
    field: K,
    value: JobDraftV2[K],
  ) => {
    const nextJobs = [...jobs];
    nextJobs[0] = { ...nextJobs[0], [field]: value };
    onUpdateJobs(nextJobs);
  };

  // Picking a uniform also seeds `uniform_instructions` from its
  // default_instructions when the field is blank or still equals another
  // uniform's default — matches the legacy behaviour.
  const selectUniform = (uniform: UniformListItem) => {
    const nextJobs = [...jobs];
    const current = nextJobs[0] || {};
    const currentInstructions = (current.uniform_instructions || '').trim();
    const isDefaultFromAnotherUniform = uniforms.some(
      (option) =>
        (option.default_instructions ?? '').trim() === currentInstructions,
    );
    const nextInstructions =
      !currentInstructions || isDefaultFromAnotherUniform
        ? (uniform.default_instructions ?? '')
        : current.uniform_instructions || '';
    nextJobs[0] = {
      ...current,
      uniform_id: uniform.id,
      uniform_instructions: nextInstructions,
    };
    onUpdateJobs(nextJobs);
  };

  const selectedUniform = useMemo(
    () => uniforms.find((option) => option.id === job?.uniform_id) || null,
    [uniforms, job?.uniform_id],
  );

  return (
    <StepContent>
      <ContentWrap>
        <StepTitle>Add job instructions</StepTitle>

        <FieldGroup>
          <FieldLabel>
            Choose the dress code
            <RequiredMark aria-hidden>*</RequiredMark>
          </FieldLabel>
          {uniformsError ? <ErrorText>{uniformsError}</ErrorText> : null}
          <UniformGrid>
            {uniformsLoading && uniforms.length === 0 ? (
              <span style={{ color: '#5B5B5B', fontSize: 13 }}>
                Loading uniforms…
              </span>
            ) : (
              uniforms.map((uniform) => (
                <UniformTile
                  key={uniform.id}
                  type="button"
                  selected={selectedUniform?.id === uniform.id}
                  aria-pressed={selectedUniform?.id === uniform.id}
                  onClick={() => selectUniform(uniform)}
                >
                  <UniformImage>
                    {uniform.image_url ? (
                      <img src={uniform.image_url} alt={uniform.name} />
                    ) : null}
                  </UniformImage>
                  <UniformLabel>{uniform.name}</UniformLabel>
                </UniformTile>
              ))
            )}
          </UniformGrid>
        </FieldGroup>

        <FieldGroup spacing="tight">
          <FieldLabel>Dress code instructions</FieldLabel>
          <InstructionsTextarea
            aria-label="Dress code instructions"
            placeholder="Describe the dress code requirements..."
            value={job?.uniform_instructions || ''}
            onChange={(event) =>
              update('uniform_instructions', event.target.value)
            }
          />
          <TextareaActions>
            <LinkButton
              type="button"
              onClick={() =>
                onBrowseSaved(
                  'dress_code_instructions',
                  'Browse saved dress code instructions',
                  (body) => update('uniform_instructions', body),
                )
              }
            >
              Browse saved
            </LinkButton>
            <ActionDivider>
              <LinkButton
                type="button"
                disabled={!job?.uniform_instructions?.trim()}
                onClick={() =>
                  onSaveSnippet(
                    'dress_code_instructions',
                    'Save dress code instructions',
                    job?.uniform_instructions || '',
                  )
                }
              >
                Save
              </LinkButton>
            </ActionDivider>
          </TextareaActions>
        </FieldGroup>

        <FieldGroup spacing="tight">
          <FieldLabel>Add job description &amp; instructions</FieldLabel>
          <InstructionsTextarea
            aria-label="Job description and instructions"
            placeholder="Describe the job responsibilities and any special instructions..."
            value={job?.instructions || ''}
            onChange={(event) => update('instructions', event.target.value)}
          />
          <TextareaActions>
            <LinkButton
              type="button"
              onClick={() =>
                onBrowseSaved(
                  'job_description',
                  'Browse saved descriptions',
                  (body) => update('instructions', body),
                )
              }
            >
              Browse saved
            </LinkButton>
            <ActionDivider>
              <LinkButton
                type="button"
                disabled={!job?.instructions?.trim()}
                onClick={() =>
                  onSaveSnippet(
                    'job_description',
                    'Save job description',
                    job?.instructions || '',
                  )
                }
              >
                Save
              </LinkButton>
            </ActionDivider>
          </TextareaActions>
        </FieldGroup>
      </ContentWrap>
    </StepContent>
  );
};

export default InstructionsStep;
